import { ArrowLeft, Building2, Copy, ExternalLink, GripVertical, LayoutDashboard, ListVideo, Monitor, Moon, Plus, Radio, RotateCcw, Settings2, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../../auth/useAuth";
import ChurchLogo from "../../../../assets/logos/Church-Logo.png";
import LiveDisplayIcon from "../../../../assets/logos/Live Display Icon.png";
import GlobalNotificationPill from "../../../../core/components/GlobalNotificationPill";
import PanelLoadingState from "../../../../core/components/PanelLoadingState";
import useResponsive from "../../../../core/hooks/useResponsive";
import { churchMobileNavStyles } from "../../styles/churchMobileNavStyles";
import {
  clearChurchLiveScreenIdentify,
  deleteChurchLiveDisplay,
  identifyChurchLiveScreen,
  loadChurchLiveDisplay,
  loadChurchLiveScreens,
  putChurchDisplayToSleep,
  removeChurchLiveScreen,
  returnChurchDisplayToLoop,
  setChurchSleepSchedule,
  setChurchInteractiveConfig,
  toggleChurchLoopItemVisibility,
  updateChurchLoopItems,
  updateChurchLiveScreenGroup,
  updateChurchLiveScreenName,
} from "../../services/liveDisplayService";

const LIVE_DISPLAY_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    shortLabel: "Overview",
    icon: LayoutDashboard,
  },
  {
    id: "displays",
    label: "Displays",
    shortLabel: "Displays",
    icon: Monitor,
  },
  {
    id: "pre-service",
    label: "Pre-Service Loop",
    shortLabel: "Loop",
    icon: ListVideo,
  },
  {
    id: "interactive",
    label: "Interactive Screens",
    shortLabel: "Foyer",
    icon: Settings2,
  },
];

const SLEEP_DAY_OPTIONS = [
  { key: "sun", label: "Sun" },
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
];

const DEFAULT_SLEEP_SCHEDULE = {
  enabled: false,
  days: [],
  startTime: "22:00",
  endTime: "06:00",
};

const ACTIVE_SCREEN_WINDOW_MS = 1000 * 60 * 2;
const LOOP_DURATION_OPTIONS = [3, 4, 5, 6, 8, 10, 12, 15, 20, 30, 45, 60, 90, 120];

const SCREEN_GROUP_CONFIGS = [
  {
    id: "service",
    title: "Service Screens",
    description: "Sanctuary and stage displays that follow the live service controller.",
  },
  {
    id: "interactive",
    title: "Interactive Screens",
    description: "Front foyer displays for interactive lobby or kiosk experiences.",
  },
];

const INTERACTIVE_APP_OPTIONS = [
  {
    id: "members-directory",
    name: "Members Directory",
    description: "Family photo directory for foyer visitors and members.",
  },
  {
    id: "announcements",
    name: "Announcements",
    description: "Public announcement cards from the pre-service loop.",
  },
  {
    id: "events",
    name: "Events",
    description: "Upcoming event cards from the pre-service loop.",
  },
  {
    id: "surrounding-events",
    name: "Surrounding Church Events",
    description: "Shared event cards from other Oikos Church organizations.",
  },
  {
    id: "mens-duties",
    name: "Mens Duties",
    description: "Sunday duty signup and upcoming duty tags for foyer screens.",
  },
  {
    id: "preacher",
    name: "Preacher",
    description: "Preacher profile card with photo, bio, and contact details.",
  },
  {
    id: "prayer-request",
    name: "Prayer Request",
    description: "A simple public prayer request form.",
  },
  {
    id: "visitor-log",
    name: "Visitor Log",
    description: "A public check-in form for first-time guests.",
  },
  {
    id: "phone-page",
    name: "Get Connected",
    description: "A QR-code page that sends people to the Church sign-in and join request.",
  },
];

function formatTimestamp(value) {
  if (!value) return "Just now";

  try {
    return new Date(value).toLocaleString();
  } catch (_error) {
    return value;
  }
}

function getLoopItemDescription(item) {
  if (!item) return "";

  const entryCount = Array.isArray(item.entries) ? item.entries.length : 0;
  const baseDescription = item.body || item.subtitle || "";

  if (entryCount > 0) {
    const entryLabel = entryCount === 1 ? "item" : "items";
    return `${entryCount} ${entryLabel} ready for this slide. ${baseDescription}`.trim();
  }

  return baseDescription;
}

function getDisplayViewLabel(state) {
  if (state === "live") return "Live service";
  if (state === "sleep") return "Sleeping";
  if (state === "ready") return "Ready slideshow";
  return "Pre-service loop";
}

function getScreenGroupLabel(screenGroup) {
  return screenGroup === "interactive" ? "Interactive Screen" : "Service Screen";
}

function isScreenRecentlySeen(screen = {}) {
  const seenAt = new Date(screen.lastSeenAt || 0).getTime();
  return Number.isFinite(seenAt) && Date.now() - seenAt <= ACTIVE_SCREEN_WINDOW_MS;
}

function getGroupedLiveUrl(publicUrl, screenGroup = "service") {
  if (!publicUrl) return "";
  const safeScreenGroup = screenGroup === "interactive" ? "interactive" : "service";

  try {
    const url = new URL(publicUrl);
    url.searchParams.set("screenGroup", safeScreenGroup);
    url.searchParams.set("screen_group", safeScreenGroup);
    return url.toString();
  } catch (_error) {
    const separator = publicUrl.includes("?") ? "&" : "?";
    return `${publicUrl}${separator}screenGroup=${safeScreenGroup}&screen_group=${safeScreenGroup}`;
  }
}

export default function LiveDisplayPage({ useStandaloneRoutes = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPhone } = useResponsive();
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [display, setDisplay] = useState(null);
  const [screens, setScreens] = useState([]);
  const [activeSection, setActiveSection] = useState("overview");
  const [showAddScreenModal, setShowAddScreenModal] = useState(false);
  const [addScreenGroup, setAddScreenGroup] = useState("service");
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [screenNames, setScreenNames] = useState({});
  const [savingScreenNames, setSavingScreenNames] = useState({});
  const [sleepDraft, setSleepDraft] = useState(DEFAULT_SLEEP_SCHEDULE);
  const [sleepSaveState, setSleepSaveState] = useState("idle");
  const [draggedLoopItemId, setDraggedLoopItemId] = useState("");
  const screenNameSaveTimersRef = useRef({});
  const loopDurationRequestRef = useRef({});
  const sleepDraftRef = useRef(DEFAULT_SLEEP_SCHEDULE);
  const sleepScheduleSaveTimerRef = useRef(null);
  const sleepScheduleRequestRef = useRef(0);

  async function refreshPage() {
    setLoading(true);

    try {
      const result = await loadChurchLiveDisplay(user?.id);
      const nextSleepDraft = {
        ...DEFAULT_SLEEP_SCHEDULE,
        ...(result.display?.sleepSchedule || {}),
      };
      setDisplay(result.display);
      setScreens(result.screens);
      sleepDraftRef.current = nextSleepDraft;
      setSleepDraft(nextSleepDraft);
    } catch (loadError) {
      console.error("Live display load error:", loadError);
      setError(loadError?.message || "Could not load live display.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshPage();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !display?.id) {
      return undefined;
    }

    let mounted = true;
    const interval = window.setInterval(async () => {
      try {
        const nextScreens = await loadChurchLiveScreens(display);
        if (!mounted) return;

        setScreens(nextScreens || []);
      } catch (screenRefreshError) {
        console.error("Live display screen refresh error:", screenRefreshError);
      }
    }, 10000);

    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, [display?.id, user?.id]);

  useEffect(() => {
    return () => {
      Object.values(screenNameSaveTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      if (sleepScheduleSaveTimerRef.current) {
        window.clearTimeout(sleepScheduleSaveTimerRef.current);
      }
    };
  }, []);

  const visibleLoopItems = useMemo(
    () => (display?.loopItems || []).filter((item) => item?.isVisible !== false),
    [display?.loopItems]
  );

  async function handleAction(actionKey, runner, successMessage) {
    if (!display || !user?.id) return;

    setBusyAction(actionKey);
    setError("");
    setNotice("");

    try {
      const result = await runner();
      setDisplay(result.display);
      setScreens(result.screens || screens);
      setNotice(successMessage);
    } catch (actionError) {
      console.error("Live display action error:", actionError);
      setError(actionError?.message || "Action failed.");
    } finally {
      setBusyAction("");
    }
  }

  async function deleteDisplay() {
    if (!display || !user?.id) return;

    const shouldDelete = window.confirm(
      "Delete this live display and remove its connected screens?"
    );

    if (!shouldDelete) return;

    setBusyAction("delete-display");
    setError("");
    setNotice("");

    try {
      const result = await deleteChurchLiveDisplay(user.id, display);
      const nextSleepDraft = {
        ...DEFAULT_SLEEP_SCHEDULE,
        ...(result.display?.sleepSchedule || {}),
      };
      setDisplay(result.display);
      setScreens(result.screens || []);
      setSelectedScreen(null);
      setScreenNames({});
      setSavingScreenNames({});
      sleepDraftRef.current = nextSleepDraft;
      setSleepDraft(nextSleepDraft);
      setNotice("Display deleted.");
    } catch (deleteError) {
      console.error("Live display delete error:", deleteError);
      setError(deleteError?.message || "Could not delete display.");
    } finally {
      setBusyAction("");
    }
  }

  function moveLoopItemToTarget(targetItemId) {
    if (!draggedLoopItemId || draggedLoopItemId === targetItemId || !display?.loopItems?.length) {
      setDraggedLoopItemId("");
      return;
    }

    const currentItems = display.loopItems || [];
    const fromIndex = currentItems.findIndex((item) => item.id === draggedLoopItemId);
    const toIndex = currentItems.findIndex((item) => item.id === targetItemId);

    if (fromIndex < 0 || toIndex < 0) {
      setDraggedLoopItemId("");
      return;
    }

    const nextItems = [...currentItems];
    const [movedItem] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, movedItem);
    setDraggedLoopItemId("");
    setDisplay((currentDisplay) => ({
      ...currentDisplay,
      loopItems: nextItems,
    }));

    handleAction(
      "loop-order",
      () => updateChurchLoopItems(user.id, display, nextItems),
      "Pre-service loop order updated."
    );
  }

  function updateLoopItemDuration(itemId, durationSeconds) {
    if (!display || !user?.id || !itemId) return;

    const safeDuration = Number(durationSeconds) || 6;
    const requestId = (loopDurationRequestRef.current[itemId] || 0) + 1;
    const previousDisplay = display;
    const nextLoopItems = (display.loopItems || []).map((item) =>
      item.id === itemId
        ? {
            ...item,
            durationSeconds: safeDuration,
          }
        : item
    );
    const optimisticDisplay = {
      ...display,
      loopItems: nextLoopItems,
    };

    loopDurationRequestRef.current[itemId] = requestId;
    setError("");
    setNotice("");
    setDisplay(optimisticDisplay);

    updateChurchLoopItems(user.id, optimisticDisplay, nextLoopItems)
      .then((result) => {
        if (loopDurationRequestRef.current[itemId] !== requestId) return;

        setDisplay(result.display);
        setScreens(result.screens || screens);
        setNotice("Slide timing updated.");
      })
      .catch((actionError) => {
        if (loopDurationRequestRef.current[itemId] !== requestId) return;

        console.error("Live display slide timing error:", actionError);
        setError(actionError?.message || "Could not update slide timing.");
        setDisplay(previousDisplay);
      });
  }

  function saveSleepSchedule(nextSchedule) {
    if (!display || !user?.id) return;

    const requestId = sleepScheduleRequestRef.current + 1;
    sleepScheduleRequestRef.current = requestId;
    setSleepSaveState("saving");
    setError("");

    setChurchSleepSchedule(user.id, display, nextSchedule)
      .then((result) => {
        if (sleepScheduleRequestRef.current !== requestId) return;

        const nextSleepDraft = {
          ...DEFAULT_SLEEP_SCHEDULE,
          ...(result.display?.sleepSchedule || nextSchedule),
        };
        setDisplay(result.display);
        setScreens(result.screens || screens);
        sleepDraftRef.current = nextSleepDraft;
        setSleepDraft(nextSleepDraft);
        setSleepSaveState("saved");
        window.setTimeout(() => {
          if (sleepScheduleRequestRef.current === requestId) {
            setSleepSaveState("idle");
          }
        }, 900);
      })
      .catch((actionError) => {
        if (sleepScheduleRequestRef.current !== requestId) return;

        console.error("Sleep schedule save error:", actionError);
        setError(actionError?.message || "Could not save sleep schedule.");
        setSleepSaveState("error");
      });
  }

  function updateSleepSchedule(partialSchedule = {}, options = {}) {
    const currentSchedule = sleepDraftRef.current || sleepDraft || display?.sleepSchedule || DEFAULT_SLEEP_SCHEDULE;
    const nextSchedule = {
      ...DEFAULT_SLEEP_SCHEDULE,
      ...currentSchedule,
      ...partialSchedule,
    };
    const delay = typeof options.delay === "number" ? options.delay : 350;

    sleepDraftRef.current = nextSchedule;
    setSleepDraft(nextSchedule);
    setDisplay((currentDisplay) =>
      currentDisplay
        ? {
            ...currentDisplay,
            sleepSchedule: nextSchedule,
          }
        : currentDisplay
    );

    if (sleepScheduleSaveTimerRef.current) {
      window.clearTimeout(sleepScheduleSaveTimerRef.current);
    }

    sleepScheduleSaveTimerRef.current = window.setTimeout(() => {
      saveSleepSchedule(nextSchedule);
    }, delay);
  }

  function toggleSleepDay(dayKey) {
    const currentSchedule = sleepDraftRef.current || sleepDraft || display?.sleepSchedule || DEFAULT_SLEEP_SCHEDULE;
    const currentDays = Array.isArray(currentSchedule.days) ? currentSchedule.days : [];
    const nextDays = currentDays.includes(dayKey)
      ? currentDays.filter((day) => day !== dayKey)
      : [...currentDays, dayKey];

    return updateSleepSchedule({ days: nextDays }, { delay: 250 });
  }

  async function toggleInteractiveApp(appId) {
    if (!display || !user?.id) return;

    const currentConfig = display.interactiveConfig || {};
    const currentApps = currentConfig.apps || {};
    const nextConfig = {
      ...currentConfig,
      apps: {
        ...currentApps,
        [appId]: currentApps[appId] === false,
      },
    };

    setBusyAction(`interactive-${appId}`);
    setError("");
    setNotice("");

    try {
      setDisplay((currentDisplay) =>
        currentDisplay
          ? {
              ...currentDisplay,
              interactiveConfig: nextConfig,
            }
          : currentDisplay
      );

      const result = await setChurchInteractiveConfig(user.id, display, nextConfig);
      setDisplay(result.display);
      setScreens(result.screens || screens);
      setNotice("Interactive screen apps updated.");
    } catch (configError) {
      console.error("Interactive screen config error:", configError);
      setError(configError?.message || "Could not update interactive screen apps.");
    } finally {
      setBusyAction("");
    }
  }

  async function copyLiveUrl() {
    if (!display?.publicUrl) return;

    try {
      await window.navigator.clipboard.writeText(display.publicUrl);
      setNotice("Live display URL copied.");
    } catch (copyError) {
      console.error("Live URL copy error:", copyError);
      setError("Could not copy the live URL.");
    }
  }

  async function copyGroupedLiveUrl(screenGroup = "service") {
    const nextUrl = getGroupedLiveUrl(display?.publicUrl, screenGroup);
    if (!nextUrl) return;

    try {
      await window.navigator.clipboard.writeText(nextUrl);
      setNotice(`${getScreenGroupLabel(screenGroup)} URL copied.`);
    } catch (copyError) {
      console.error("Grouped live URL copy error:", copyError);
      setError("Could not copy the live URL.");
    }
  }

  function openAddScreenModal(screenGroup = "service") {
    setAddScreenGroup(screenGroup === "interactive" ? "interactive" : "service");
    setShowAddScreenModal(true);
  }

  async function saveScreenName(screen, rawName, options = {}) {
    if (!display || !screen?.screenCode) return;

    const nextName = String(rawName ?? screen.name ?? "").trim() || "Live screen";
    setError("");
    if (options.showNotice) {
      setNotice("");
    }
    setSavingScreenNames((currentSaving) => ({
      ...currentSaving,
      [screen.screenCode]: true,
    }));

    try {
      await updateChurchLiveScreenName(display, screen.screenCode, nextName);
      setScreens((currentScreens) =>
        currentScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, name: nextName }
            : currentScreen
        )
      );
      if (options.showNotice) {
        setNotice("Screen name saved.");
      }
    } catch (renameError) {
      console.error("Live display screen rename error:", renameError);
      setError(renameError?.message || "Could not rename screen.");
    } finally {
      setSavingScreenNames((currentSaving) => {
        const nextSaving = { ...currentSaving };
        delete nextSaving[screen.screenCode];
        return nextSaving;
      });
    }
  }

  function updateScreenNameDraft(screen, nextName) {
    if (!screen?.screenCode) return;

    setScreenNames((currentNames) => ({
      ...currentNames,
      [screen.screenCode]: nextName,
    }));

    window.clearTimeout(screenNameSaveTimersRef.current[screen.screenCode]);
    screenNameSaveTimersRef.current[screen.screenCode] = window.setTimeout(() => {
      saveScreenName(screen, nextName);
    }, 600);
  }

  function flushScreenName(screen) {
    if (!screen?.screenCode) return;

    window.clearTimeout(screenNameSaveTimersRef.current[screen.screenCode]);
    delete screenNameSaveTimersRef.current[screen.screenCode];
    saveScreenName(screen, screenNames[screen.screenCode] ?? screen.name ?? "");
  }

  async function moveScreenToGroup(screen, screenGroup) {
    if (!display || !screen?.screenCode) return;

    const currentGroup = screen.screenGroup || "service";
    if (currentGroup === screenGroup) return;

    setBusyAction(`group-${screen.screenCode}`);
    setError("");
    setNotice("");

    try {
      setScreens((currentScreens) =>
        currentScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, screenGroup }
            : currentScreen
        )
      );

      const result = await updateChurchLiveScreenGroup(display, screen.screenCode, screenGroup);
      setScreens(result.screens || screens);
      setSelectedScreen((currentScreen) =>
        currentScreen?.screenCode === screen.screenCode
          ? { ...currentScreen, screenGroup }
          : currentScreen
      );
      setNotice(
        screenGroup === "interactive"
          ? `${screen.name || "Screen"} moved to interactive screens.`
          : `${screen.name || "Screen"} moved to service screens.`
      );
    } catch (groupError) {
      console.error("Live display screen group error:", groupError);
      setError(groupError?.message || "Could not update the screen group.");
      setScreens((currentScreens) =>
        currentScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, screenGroup: currentGroup }
            : currentScreen
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function identifyScreen(screen) {
    if (!display || !screen?.screenCode || !user?.id) return;

    const nextStatus = screen.status === "identify" ? "connected" : "identify";
    let loopResult = null;
    setBusyAction(`identify-${screen.screenCode}`);
    setError("");
    setNotice("");

    try {
      setScreens((currentScreens) =>
        currentScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, status: nextStatus }
            : currentScreen
        )
      );

      if (nextStatus === "identify") {
        await identifyChurchLiveScreen(display, screen.screenCode);
      } else {
        await clearChurchLiveScreenIdentify(display, screen.screenCode);
        returnChurchDisplayToLoop(user.id, display)
          .then((result) => {
            if (result?.display) {
              setDisplay(result.display);
            }

            if (result?.screens?.length) {
              setScreens((currentScreens) =>
                result.screens.map((currentScreen) =>
                  currentScreen.screenCode === screen.screenCode
                    ? { ...currentScreen, status: nextStatus }
                    : currentScreen
                )
              );
            }
          })
          .catch((loopError) => {
            console.error("Live display return-to-loop after identify error:", loopError);
          });
      }

      if (loopResult?.display) {
        setDisplay(loopResult.display);
      }

      setScreens((currentScreens) =>
        (loopResult?.screens?.length ? loopResult.screens : currentScreens).map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, status: nextStatus }
            : currentScreen
        )
      );
      setNotice(
        nextStatus === "identify"
          ? `Identifying ${screen.name || "screen"}.`
          : `Stopped identifying ${screen.name || "screen"} and returned to the pre-service loop.`
      );
    } catch (identifyError) {
      console.error("Live display screen identify error:", identifyError);
      setError(identifyError?.message || "Could not update identify mode.");
    } finally {
      setBusyAction("");
    }
  }

  async function sendScreenToPreServiceLoop(screen) {
    if (!display || !screen?.screenCode || !user?.id) return;

    setBusyAction(`loop-${screen.screenCode}`);
    setError("");
    setNotice("");

    try {
      if (screen.status === "identify") {
        await clearChurchLiveScreenIdentify(display, screen.screenCode);
      }

      if ((screen.screenGroup || "service") !== "service") {
        await updateChurchLiveScreenGroup(display, screen.screenCode, "service");
      }

      const result = await returnChurchDisplayToLoop(user.id, display);
      setDisplay(result.display);
      setScreens(
        (result.screens || screens).map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, screenGroup: "service" }
            : currentScreen
        )
      );
      setSelectedScreen(null);
      setNotice(`${screen.name || "Screen"} sent to the pre-service loop.`);
    } catch (loopError) {
      console.error("Live display screen loop error:", loopError);
      setError(loopError?.message || "Could not send screen to the pre-service loop.");
    } finally {
      setBusyAction("");
    }
  }

  async function removeScreen(screen) {
    if (!display || !screen?.screenCode) return;

    setBusyAction(`remove-${screen.screenCode}`);
    setError("");
    setNotice("");

    try {
      const result = await removeChurchLiveScreen(display, screen.screenCode, screen.id);
      setScreens(result.screens || []);
      setSelectedScreen(null);
      setScreenNames((currentNames) => {
        const nextNames = { ...currentNames };
        delete nextNames[screen.screenCode];
        return nextNames;
      });
      setSavingScreenNames((currentSaving) => {
        const nextSaving = { ...currentSaving };
        delete nextSaving[screen.screenCode];
        return nextSaving;
      });
      window.clearTimeout(screenNameSaveTimersRef.current[screen.screenCode]);
      delete screenNameSaveTimersRef.current[screen.screenCode];
      setNotice(`${screen.name || "Screen"} removed.`);
    } catch (removeError) {
      console.error("Live display screen remove error:", removeError);
      setError(removeError?.message || "Could not remove screen.");
    } finally {
      setBusyAction("");
    }
  }

  const showOverview = !useStandaloneRoutes || activeSection === "overview";
  const showDisplays = !useStandaloneRoutes || activeSection === "displays";
  const showPreService = !useStandaloneRoutes || activeSection === "pre-service";
  const showInteractive = !useStandaloneRoutes || activeSection === "interactive";
  const displayViewLabel = getDisplayViewLabel(display?.state);
  const readyStatusLabel = display?.isReady ? "Ready" : "Needs approval";
  const selectedScreenRecord = selectedScreen
    ? screens.find(
        (screen) =>
          screen.screenCode === selectedScreen.screenCode ||
          screen.id === selectedScreen.id
      ) || selectedScreen
    : null;
  const screensByGroup = SCREEN_GROUP_CONFIGS.reduce((groups, groupConfig) => {
    groups[groupConfig.id] = screens.filter(
      (screen) => (screen.screenGroup || "service") === groupConfig.id
    );
    return groups;
  }, {});
  const sleepSchedule = {
    ...DEFAULT_SLEEP_SCHEDULE,
    ...(display?.sleepSchedule || {}),
    ...(sleepDraft || {}),
  };
  const sleepDays = Array.isArray(sleepSchedule.days) ? sleepSchedule.days : [];
  const interactiveApps = display?.interactiveConfig?.apps || {};
  const enabledInteractiveAppCount = INTERACTIVE_APP_OPTIONS.filter(
    (app) => interactiveApps[app.id] !== false
  ).length;
  const workspaceName =
    user?.churchOrgSession?.account?.name ||
    user?.account?.name ||
    user?.organization?.name ||
    display?.organizationName ||
    "Church Workspace";
  const organizationLogoUrl =
    user?.churchOrgSession?.account?.logo_url ||
    user?.churchOrgSession?.account?.logoUrl ||
    user?.account?.logo_url ||
    user?.account?.logoUrl ||
    user?.organization?.logo_url ||
    user?.organization?.logoUrl ||
    display?.organizationLogoUrl ||
    ChurchLogo;

  if (loading) {
    return (
      <PanelLoadingState
        iconAlt="Live Display"
        iconSrc={LiveDisplayIcon}
        title="Loading Live Display"
        detail="Preparing your screens, pre-service loop, and live takeover controls..."
      />
    );
  }

  return (
    <div style={useStandaloneRoutes ? { ...styles.wrapper, ...(isPhone ? styles.wrapperPhone : {}) } : styles.page}>
      <div
        style={
          useStandaloneRoutes
            ? { ...styles.managementHeader, ...(isPhone ? styles.managementHeaderPhone : {}) }
            : { ...styles.pageHeader, ...(isPhone ? styles.pageHeaderPhone : {}) }
        }
      >
        {useStandaloneRoutes ? (
          <>
            <div style={{ ...styles.managementHeaderMain, ...(isPhone ? styles.managementHeaderMainPhone : {}) }}>
              <img src={LiveDisplayIcon} alt="Live Display" style={styles.managementHeaderIcon} />
              <div style={styles.managementHeaderCopy}>
                <div style={styles.managementHeaderEyebrow}>Oikos Church Live Display</div>
                <div style={styles.managementHeaderName}>{workspaceName}</div>
                <div style={styles.managementHeaderChurchName}>
                  Service ID: {display?.serviceId || "current_service"}
                </div>
              </div>
            </div>
            <div style={{ ...styles.managementHeaderMeta, ...(isPhone ? styles.managementHeaderMetaPhone : {}) }}>
              <div style={styles.managementHeaderWelcomeLabel}>Updated</div>
              <div style={styles.managementHeaderUserName}>{formatTimestamp(display?.updatedAt)}</div>
              <button
                type="button"
                style={{ ...styles.managementHeaderIdRow, ...(isPhone ? styles.managementHeaderIdRowPhone : {}) }}
                onClick={() =>
                  handleAction(
                    "loop",
                    () => returnChurchDisplayToLoop(user.id, display),
                    "Pre-service loop started."
                  )
                }
                disabled={busyAction === "loop"}
              >
                <Radio size={15} />
                <span>{busyAction === "loop" ? "Starting..." : "Start Pre-Service Loop"}</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <h2 style={styles.pageTitle}>Live Display</h2>
              <div style={styles.pageMeta}>
                Service ID: {display?.serviceId || "current_service"} • Updated {formatTimestamp(display?.updatedAt)}
              </div>
            </div>
            <button
              style={styles.refreshButton}
              onClick={() =>
                handleAction(
                  "loop",
                  () => returnChurchDisplayToLoop(user.id, display),
                  "Pre-service loop started."
                )
              }
              disabled={busyAction === "loop"}
            >
              {busyAction === "loop" ? "Starting..." : "Start Pre-Service Loop"}
            </button>
          </>
        )}
      </div>

      <GlobalNotificationPill
        message={error || notice}
        onDismiss={() => {
          if (error) {
            setError("");
          } else {
            setNotice("");
          }
        }}
        tone={error ? "error" : "success"}
      />
      <style>
        {`
          @keyframes oikosScreenIdentifyButtonRipple {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.2);
            }
            16% {
              opacity: 0.86;
              transform: translate(-50%, -50%) scale(0.34);
            }
            70% {
              opacity: 0.34;
              transform: translate(-50%, -50%) scale(0.96);
            }
            100% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(1.22);
            }
          }
        `}
      </style>

      <div style={useStandaloneRoutes ? styles.liveDisplayShell : styles.embeddedContentShell}>
        {useStandaloneRoutes ? (
          <aside style={{ ...styles.sideNav, ...(isPhone ? styles.sideNavPhone : {}) }} aria-label="Live Display sections">
            <div style={{ ...styles.sideNavBrand, ...(isPhone ? styles.sideNavBrandPhone : {}) }}>
              <img src={ChurchLogo} alt="Oikos Church" style={styles.sideNavBrandLogo} />
              <span style={styles.sideNavBrandLine} />
            </div>
            <div style={{ ...styles.sideNavOrg, ...(isPhone ? styles.sideNavOrgPhone : {}) }}>
              <img src={organizationLogoUrl} alt={workspaceName} style={styles.sideNavLogo} />
              <div style={styles.sideNavOrgCopy}>
                <span style={styles.sideNavOrgLabel}>Organization</span>
                <strong style={styles.sideNavOrgName}>{workspaceName}</strong>
              </div>
            </div>
            <div style={{ ...styles.sideNavDivider, ...(isPhone ? styles.sideNavDividerPhone : {}) }} />
            <div style={{ ...styles.navMain, ...(isPhone ? styles.navMainPhone : {}) }}>
              {LIVE_DISPLAY_SECTIONS.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.id;

                return (
                  <button
                    key={section.id}
                    type="button"
                    style={{
                      ...styles.navButton,
                      ...(isPhone ? styles.navButtonPhone : {}),
                      ...(active ? styles.navButtonActive : {}),
                      ...(active ? { background: "var(--color-primary)" } : {}),
                      ...(isPhone && active ? styles.navButtonActivePhone : {}),
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.currentTarget.blur();
                      setActiveSection(section.id);
                    }}
                  >
                    <Icon size={isPhone ? 15 : 17} />
                    <span style={isPhone ? styles.navLabelPhone : null}>
                      {isPhone ? section.shortLabel || section.label : section.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ ...styles.sideNavFooter, ...(isPhone ? styles.sideNavFooterPhone : {}) }}>
              <button
                type="button"
                style={{ ...styles.footerButton, ...(isPhone ? styles.footerButtonPhone : {}) }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.currentTarget.blur();
                  navigate("/church");
                }}
              >
                <ArrowLeft size={isPhone ? 15 : 16} />
                <span style={isPhone ? styles.navLabelPhone : null}>Apps</span>
              </button>
              {!isPhone ? (
                <button
                  type="button"
                  style={styles.footerButton}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => navigate("/church/management")}
                >
                  <Building2 size={16} />
                  <span>Management</span>
                </button>
              ) : null}
            </div>
          </aside>
        ) : null}

        <div
          style={
            useStandaloneRoutes
              ? { ...styles.liveDisplayContent, ...(isPhone ? styles.liveDisplayContentPhone : {}) }
              : styles.embeddedLiveDisplayContent
          }
        >
      {showOverview ? (
        <>
      <div style={{ ...styles.liveControllerToolbar, ...(isPhone ? styles.liveControllerToolbarPhone : {}) }}>
        <div>
          <div style={styles.toolbarTitle}>Controller</div>
          <div style={styles.toolbarMeta}>Tap a tile to manage displays, loops, and foyer screens.</div>
        </div>
        <div style={{ ...styles.toolbarActions, ...(isPhone ? styles.toolbarActionsPhone : {}) }}>
          <button
            type="button"
            style={styles.actionButton}
            onClick={() =>
              handleAction(
                "loop",
                () => returnChurchDisplayToLoop(user.id, display),
                "Pre-service loop started."
              )
            }
            disabled={busyAction === "loop"}
          >
            <Radio size={15} />
            {busyAction === "loop" ? "Starting..." : "Start Loop"}
          </button>
          <button
            type="button"
            style={styles.actionButton}
            onClick={() =>
              handleAction(
                "sleep-now",
                () => putChurchDisplayToSleep(user.id, display),
                "All screens are sleeping."
              )
            }
            disabled={busyAction === "sleep-now"}
          >
            <Moon size={15} />
            {busyAction === "sleep-now" ? "Sleeping..." : "Sleep All"}
          </button>
        </div>
      </div>

      <section style={styles.tileGroup}>
        <div style={styles.groupTitle}>Live Display</div>
        <div style={{ ...styles.cleanTileGrid, ...(isPhone ? styles.cleanTileGridPhone : {}) }}>
          <button type="button" style={styles.cleanTile} onClick={copyLiveUrl}>
            <div style={styles.cleanTileHeader}>
              <span style={styles.cleanIconWrap}><Copy size={18} /></span>
              <span style={styles.cleanTileLabel}>Live Link</span>
            </div>
            <div style={styles.cleanTileValue}>{display?.publicCode}</div>
            <div style={styles.cleanTileDetail}>Tap to copy the public display URL.</div>
          </button>

          <button type="button" style={styles.cleanTile} onClick={() => setActiveSection("displays")}>
            <div style={styles.cleanTileHeader}>
              <span style={styles.cleanIconWrap}><Monitor size={18} /></span>
              <span style={styles.cleanTileLabel}>Connected Screens</span>
            </div>
            <div style={styles.cleanTileValue}>{screens.length}</div>
            <div style={styles.cleanTileDetail}>Service and interactive screens currently online.</div>
          </button>

          <button type="button" style={styles.cleanTile} onClick={() => setActiveSection("pre-service")}>
            <div style={styles.cleanTileHeader}>
              <span style={styles.cleanIconWrap}><ListVideo size={18} /></span>
              <span style={styles.cleanTileLabel}>Pre-Service Loop</span>
            </div>
            <div style={styles.cleanTileValue}>{visibleLoopItems.length} cards</div>
            <div style={styles.cleanTileDetail}>Edit the rotating slides shown before service.</div>
          </button>

          <button type="button" style={styles.cleanTile} onClick={() => setActiveSection("interactive")}>
            <div style={styles.cleanTileHeader}>
              <span style={styles.cleanIconWrap}><Settings2 size={18} /></span>
              <span style={styles.cleanTileLabel}>Interactive Foyer</span>
            </div>
            <div style={styles.cleanTileValue}>{enabledInteractiveAppCount} apps</div>
            <div style={styles.cleanTileDetail}>Choose which public kiosk apps appear on foyer screens.</div>
          </button>
        </div>
      </section>

      <section style={styles.tileGroup}>
        <div style={styles.groupTitle}>Status</div>
        <div style={{ ...styles.cleanTileGrid, ...(isPhone ? styles.cleanTileGridPhone : {}) }}>
          <div style={styles.cleanTile}>
            <div style={styles.cleanTileHeader}>
              <span style={styles.cleanIconWrap}><Radio size={18} /></span>
              <span style={styles.cleanTileLabel}>Display Status</span>
            </div>
            <div style={styles.cleanTileValue}>{readyStatusLabel}</div>
            <div style={styles.cleanTileDetail}>Current view is {displayViewLabel}.</div>
          </div>

          <div style={styles.cleanTile}>
            <div style={styles.cleanTileHeader}>
              <span style={styles.cleanIconWrap}><Moon size={18} /></span>
              <span style={styles.cleanTileLabel}>Sleep Schedule</span>
            </div>
            <div style={styles.cleanTileValue}>
              <span
                aria-label={`Sleep schedule ${sleepSchedule.enabled ? "on" : "off"}`}
                style={{
                  ...styles.switchButton,
                  cursor: "default",
                  ...(sleepSchedule.enabled ? styles.switchButtonOn : styles.switchButtonOff),
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    ...styles.switchKnob,
                    ...(sleepSchedule.enabled ? styles.switchKnobOn : {}),
                  }}
                />
                <span style={styles.switchText}>{sleepSchedule.enabled ? "On" : "Off"}</span>
              </span>
            </div>
            <div style={styles.cleanTileDetail}>
              {sleepSchedule.enabled && sleepDays.length > 0
                ? `${sleepSchedule.startTime} to ${sleepSchedule.endTime}`
                : "No scheduled sleep window is active."}
            </div>
          </div>
        </div>
      </section>
        </>
      ) : null}

      <div
        style={{
          ...styles.layout,
          ...((useStandaloneRoutes && showOverview) || showDisplays || showPreService || showInteractive ? styles.layoutSingle : {}),
          ...(isPhone ? styles.layoutPhone : {}),
        }}
      >
        <div style={styles.leftColumn}>
          {showDisplays ? (
          <>
          <div style={styles.controllerSurface}>
            <div style={{ ...styles.controllerHeader, ...(isPhone ? styles.controllerHeaderPhone : {}) }}>
              <div style={styles.controllerHeaderCopy}>
                <div style={styles.cardTitle}>Screen Controller</div>
                <div style={styles.cardMeta}>
                  Manage connected screens, locate displays, and send screens back to the pre-service loop.
                </div>
              </div>
              <div style={{ ...styles.controllerHeaderActions, ...(isPhone ? styles.controllerHeaderActionsPhone : {}) }}>
                <button
                  type="button"
                  style={styles.primaryButton}
                  onClick={() =>
                    handleAction(
                      "sleep-now",
                      () => putChurchDisplayToSleep(user.id, display),
                      "All screens are sleeping."
                    )
                  }
                  disabled={busyAction === "sleep-now"}
                >
                  <Moon size={16} />
                  {busyAction === "sleep-now" ? "Sleeping..." : "Sleep All Screens"}
                </button>
              </div>
            </div>

            <div style={styles.controllerTileGrid}>
              <div style={{ ...styles.controllerInfoTile, ...styles.controllerInfoTileFull }}>
                <span style={styles.controllerTileTop}>
                  <span style={styles.controllerTileIcon}>
                    <Moon size={22} />
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={sleepSchedule.enabled}
                    style={{
                      ...styles.switchButton,
                      ...(sleepSchedule.enabled ? styles.switchButtonOn : styles.switchButtonOff),
                      ...(busyAction === "sleep-schedule" ? styles.switchButtonBusy : {}),
                    }}
                    onClick={() => updateSleepSchedule({ enabled: !sleepSchedule.enabled }, { delay: 0 })}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        ...styles.switchKnob,
                        ...(sleepSchedule.enabled ? styles.switchKnobOn : {}),
                      }}
                    />
                    <span style={styles.switchText}>{sleepSchedule.enabled ? "On" : "Off"}</span>
                  </button>
                </span>
                <strong style={styles.controllerTileTitle}>Sleep Schedule</strong>
                <span style={styles.controllerTileBody}>
                  {sleepSchedule.enabled && sleepDays.length > 0
                    ? `${sleepSchedule.startTime} to ${sleepSchedule.endTime}`
                    : "Connected displays show a black sleep screen during selected times."}
                </span>

                <span style={{ ...styles.sleepSettingsGrid, ...(isPhone ? styles.sleepSettingsGridPhone : {}) }}>
                  <label style={styles.fieldLabel}>
                    Start
                    <input
                      type="time"
                      value={sleepSchedule.startTime}
                      onChange={(event) => updateSleepSchedule({ startTime: event.target.value })}
                      style={styles.timeInput}
                    />
                  </label>
                  <label style={styles.fieldLabel}>
                    End
                    <input
                      type="time"
                      value={sleepSchedule.endTime}
                      onChange={(event) => updateSleepSchedule({ endTime: event.target.value })}
                      style={styles.timeInput}
                    />
                  </label>
                </span>

                <span style={styles.sleepDays}>
                  {SLEEP_DAY_OPTIONS.map((day) => {
                    const selected = sleepDays.includes(day.key);

                    return (
                      <button
                        key={day.key}
                        type="button"
                        style={{
                          ...styles.sleepDayButton,
                          ...(selected ? styles.sleepDayButtonActive : {}),
                        }}
                        onClick={() => toggleSleepDay(day.key)}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </span>
              </div>

              {SCREEN_GROUP_CONFIGS.map((groupConfig) => (
                <section key={groupConfig.id} style={styles.screenGroupSection}>
                  <div style={styles.screenGroupHeader}>
                    <div>
                      <div style={styles.screenGroupTitle}>{groupConfig.title}</div>
                      <div style={styles.screenGroupMeta}>{groupConfig.description}</div>
                    </div>
                    <span style={styles.screenGroupCount}>
                      {(screensByGroup[groupConfig.id] || []).length}
                    </span>
                  </div>
                  {(screensByGroup[groupConfig.id] || []).length > 0 ? (
                    <div style={styles.screenGroupGrid}>
                      <button
                        type="button"
                        style={{ ...styles.screenTile, ...styles.addScreenTile }}
                        onClick={() => openAddScreenModal(groupConfig.id)}
                      >
                        <span style={styles.addScreenIcon}>
                          <Plus size={36} />
                        </span>
                        <strong style={styles.screenTileTitle}>Add {groupConfig.id === "interactive" ? "interactive" : "service"} screen</strong>
                        <span style={styles.screenTileMeta}>
                          Link opens as a {groupConfig.id === "interactive" ? "foyer kiosk" : "service"} screen automatically.
                        </span>
                      </button>
                      {(screensByGroup[groupConfig.id] || []).map((screen) => {
                        const isOnline = isScreenRecentlySeen(screen);
                        return (
                          <button
                            key={screen.id || screen.screenCode}
                            type="button"
                            style={styles.screenTile}
                            onClick={() => setSelectedScreen(screen)}
                          >
                            <span style={styles.controllerTileTop}>
                              <span style={styles.screenTileIcon}>
                                <Monitor size={22} />
                              </span>
                              <span
                                style={{
                                  ...styles.screenStatusPill,
                                  ...(!isOnline && screen.status !== "identify" ? styles.screenStatusPillOffline : {}),
                                }}
                              >
                                {screen.status === "identify" ? "Locating" : isOnline ? "Online" : "Offline"}
                              </span>
                            </span>
                            <strong style={styles.screenTileTitle}>
                              {screenNames[screen.screenCode] ?? screen.name ?? "Live screen"}
                            </strong>
                            <span style={styles.screenTileDetails}>
                              <span style={styles.screenTileDetailRow}>
                                <span style={styles.screenTileDetailLabel}>Mode</span>
                                <span style={styles.screenTileDetailValue}>
                                  {groupConfig.id === "interactive" ? "Interactive foyer" : displayViewLabel}
                                </span>
                              </span>
                              <span style={styles.screenTileDetailRow}>
                                <span style={styles.screenTileDetailLabel}>Seen</span>
                                <span style={styles.screenTileDetailValue}>{formatTimestamp(screen.lastSeenAt)}</span>
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={styles.screenGroupGrid}>
                      <button
                        type="button"
                        style={{ ...styles.screenTile, ...styles.addScreenTile }}
                        onClick={() => openAddScreenModal(groupConfig.id)}
                      >
                        <span style={styles.addScreenIcon}>
                          <Plus size={36} />
                        </span>
                        <strong style={styles.screenTileTitle}>Add {groupConfig.id === "interactive" ? "interactive" : "service"} screen</strong>
                        <span style={styles.screenTileMeta}>
                          Link opens as a {groupConfig.id === "interactive" ? "foyer kiosk" : "service"} screen automatically.
                        </span>
                      </button>
                      <div style={styles.emptyScreenGroup}>
                        {groupConfig.id === "interactive"
                          ? "No interactive foyer screens yet."
                          : "No service screens connected yet."}
                      </div>
                    </div>
                  )}
                </section>
              ))}

              {screens.length === 0 ? (
                <div style={styles.emptyScreenTile}>
                  No screens connected yet. Add a screen, then open the live URL on the display PC.
                </div>
              ) : null}
            </div>
          </div>

          </>
          ) : null}

          {showPreService ? (
          <div style={{ ...styles.controlCard, ...styles.preServiceControlCard }}>
            <div style={styles.preServiceIntro}>
              <div style={styles.cardTitle}>Pre-Service Loop</div>
              <div style={styles.cardMeta}>
                This is the rotating lobby display before service starts. It can show your church logo, welcome message, announcements, events, giving summary, current hymns, and a family photo mirage from member family images. Drag slides into order, choose each slide's time, and turn any slide on or off.
              </div>
            </div>

            <div style={styles.loopList}>
              {(display?.loopItems || []).map((item) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(event) => {
                    setDraggedLoopItemId(item.id);
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", item.id);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    moveLoopItemToTarget(item.id);
                  }}
                  onDragEnd={() => setDraggedLoopItemId("")}
                  style={{
                    ...styles.loopCard,
                    ...(draggedLoopItemId === item.id ? styles.loopCardDragging : {}),
                  }}
                >
                  <div style={styles.loopHeader}>
                    <div style={styles.loopDragHandle} aria-hidden="true">
                      <GripVertical size={18} />
                    </div>
                    <div style={styles.loopSummary}>
                      <div style={styles.loopTone}>{item.tone || "Notification"}</div>
                      <div style={styles.loopTitle}>{item.title}</div>
                      <div style={styles.loopBody}>{getLoopItemDescription(item)}</div>
                    </div>
                    <div style={styles.loopControls}>
                      <label style={styles.loopTimeLabel}>
                        Time
                        <select
                          style={styles.loopTimeSelect}
                          value={item.durationSeconds || display?.loopIntervalSeconds || 6}
                          onChange={(event) => updateLoopItemDuration(item.id, event.target.value)}
                        >
                          {LOOP_DURATION_OPTIONS.map((seconds) => (
                            <option key={seconds} value={seconds}>
                              {seconds}s
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={item.isVisible !== false}
                        aria-label={`${item.title || "Slide"} ${item.isVisible === false ? "off" : "on"}`}
                        style={{
                          ...styles.switchButton,
                          ...(item.isVisible === false ? styles.switchButtonOff : styles.switchButtonOn),
                          ...(busyAction === `loop-${item.id}` ? styles.switchButtonBusy : {}),
                        }}
                        onClick={() =>
                          handleAction(
                            `loop-${item.id}`,
                            () => toggleChurchLoopItemVisibility(user.id, display, item.id),
                            item.isVisible === false ? "Slide shown in pre-service loop." : "Slide hidden from pre-service loop."
                          )
                        }
                        disabled={busyAction === `loop-${item.id}`}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            ...styles.switchKnob,
                            ...(item.isVisible !== false ? styles.switchKnobOn : {}),
                          }}
                        />
                        <span style={styles.switchText}>{item.isVisible === false ? "Off" : "On"}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          ) : null}

          {showInteractive ? (
          <div style={{ ...styles.controlCard, ...styles.interactiveControlCard }}>
            <div style={{ ...styles.controlHeader, ...(isPhone ? styles.controlHeaderPhone : {}) }}>
              <div>
                <div style={styles.cardTitle}>Interactive Foyer Apps</div>
                <div style={styles.cardMeta}>
                  Choose which public apps appear when someone touches an interactive foyer screen. Interactive screens keep the slide-style welcome loop, then open this app menu on touch.
                </div>
              </div>
              <div style={styles.codeBadge}>{enabledInteractiveAppCount} on</div>
            </div>

            <div style={{ ...styles.interactivePreviewRow, ...(isPhone ? styles.interactivePreviewRowPhone : {}) }}>
              <div style={styles.interactivePreviewTile}>
                <span style={styles.controllerTileTop}>
                  <span style={styles.controllerTileIcon}>
                    <Monitor size={22} />
                  </span>
                  <span style={styles.screenStatusPill}>Public</span>
                </span>
                <strong style={styles.controllerTileTitle}>Foyer Menu</strong>
                <span style={styles.controllerTileBody}>
                  Opens at {display?.publicUrl ? `${display.publicUrl}/interactive/members-directory` : "/live/.../interactive/members-directory"} and stays available from every interactive screen.
                </span>
                <span style={styles.controllerTileActions}>
                  <a
                    href={display?.publicUrl ? `${display.publicUrl}/interactive/members-directory` : "#"}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.inlineTileLink}
                  >
                    <ExternalLink size={14} />
                    Open Directory
                  </a>
                </span>
              </div>
              <div style={styles.interactivePreviewTile}>
                <span style={styles.controllerTileTop}>
                  <span style={styles.controllerTileIcon}>
                    <Settings2 size={22} />
                  </span>
                  <span style={styles.screenStatusPill}>Touchscreen</span>
                </span>
                <strong style={styles.controllerTileTitle}>Display Behavior</strong>
                <span style={styles.controllerTileBody}>
                  The foyer screen cycles Members, Announcements, and Events slides when those apps are enabled. A bottom tag invites guests to touch the screen.
                </span>
              </div>
            </div>

            <div style={styles.interactiveAppGrid}>
              {INTERACTIVE_APP_OPTIONS.map((app) => {
                const enabled = interactiveApps[app.id] !== false;

                return (
                  <div key={app.id} style={styles.interactiveAppCard}>
                    <div style={styles.interactiveAppCopy}>
                      <div style={styles.interactiveAppTitle}>{app.name}</div>
                      <div style={styles.interactiveAppDescription}>{app.description}</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      aria-label={`${app.name} ${enabled ? "on" : "off"}`}
                      style={{
                        ...styles.switchButton,
                        ...(enabled ? styles.switchButtonOn : styles.switchButtonOff),
                        ...(busyAction === `interactive-${app.id}` ? styles.switchButtonBusy : {}),
                      }}
                      onClick={() => toggleInteractiveApp(app.id)}
                      disabled={busyAction === `interactive-${app.id}`}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          ...styles.switchKnob,
                          ...(enabled ? styles.switchKnobOn : {}),
                        }}
                      />
                      <span style={styles.switchText}>{enabled ? "On" : "Off"}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          ) : null}

        </div>

        <div style={styles.rightColumn}>
        </div>
      </div>
        </div>
      </div>
      {selectedScreenRecord ? (
        <div style={styles.modalOverlay} role="presentation" onMouseDown={() => setSelectedScreen(null)}>
          <div
            style={{ ...styles.screenModalCard, ...(isPhone ? styles.modalCardPhone : {}) }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="screen-controller-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div style={styles.screenModalTitleBlock}>
                <span style={styles.screenTileIcon}>
                  <Monitor size={22} />
                </span>
                <div>
                  <div id="screen-controller-title" style={styles.modalTitle}>
                    {screenNames[selectedScreenRecord.screenCode] ?? selectedScreenRecord.name ?? "Live screen"}
                  </div>
                  <div style={styles.modalMeta}>
                    {selectedScreenRecord.status === "identify"
                      ? "Locating screen"
                      : (selectedScreenRecord.screenGroup || "service") === "interactive"
                        ? "Interactive foyer screen"
                        : displayViewLabel}
                  </div>
                </div>
              </div>
              <button type="button" style={styles.iconButton} onClick={() => setSelectedScreen(null)} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <label style={styles.screenNameLabel}>
              Screen Name
              <input
                style={{ ...styles.screenNameInput, ...styles.screenModalNameInput }}
                value={screenNames[selectedScreenRecord.screenCode] ?? selectedScreenRecord.name ?? ""}
                onChange={(event) => updateScreenNameDraft(selectedScreenRecord, event.target.value)}
                onBlur={() => flushScreenName(selectedScreenRecord)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.currentTarget.blur();
                  }
                }}
              />
              {savingScreenNames[selectedScreenRecord.screenCode] ? (
                <span style={styles.screenNameSaving}>Saving...</span>
              ) : null}
            </label>

            <div style={styles.screenModalDetails}>
              <div style={styles.screenTileDetailRow}>
                <span style={styles.screenTileDetailLabel}>Screen ID</span>
                <span style={styles.screenTileDetailValue}>{selectedScreenRecord.screenCode || selectedScreenRecord.id || "Unknown"}</span>
              </div>
              <div style={styles.screenTileDetailRow}>
                <span style={styles.screenTileDetailLabel}>Device</span>
                <span style={styles.screenTileDetailValue}>{selectedScreenRecord.deviceInfo || "Display device"}</span>
              </div>
              <div style={styles.screenTileDetailRow}>
                <span style={styles.screenTileDetailLabel}>Group</span>
                <span style={styles.screenTileDetailValue}>
                  {(selectedScreenRecord.screenGroup || "service") === "interactive"
                    ? "Interactive Screens"
                    : "Service Screens"}
                </span>
              </div>
              <div style={styles.screenTileDetailRow}>
                <span style={styles.screenTileDetailLabel}>Last Seen</span>
                <span style={styles.screenTileDetailValue}>{formatTimestamp(selectedScreenRecord.lastSeenAt)}</span>
              </div>
            </div>

            <div style={styles.screenModalGroupActions}>
              <button
                type="button"
                style={{
                  ...styles.groupChoiceButton,
                  ...((selectedScreenRecord.screenGroup || "service") === "service" ? styles.groupChoiceButtonActive : {}),
                }}
                onClick={() => moveScreenToGroup(selectedScreenRecord, "service")}
                disabled={busyAction === `group-${selectedScreenRecord.screenCode}`}
              >
                Service Screen
              </button>
              <button
                type="button"
                style={{
                  ...styles.groupChoiceButton,
                  ...((selectedScreenRecord.screenGroup || "service") === "interactive" ? styles.groupChoiceButtonActive : {}),
                }}
                onClick={() => moveScreenToGroup(selectedScreenRecord, "interactive")}
                disabled={busyAction === `group-${selectedScreenRecord.screenCode}`}
              >
                Interactive Foyer
              </button>
            </div>

            <div style={styles.screenModalActions}>
              <button
                type="button"
                style={{
                  ...styles.actionModalSecondary,
                  ...(selectedScreenRecord.status === "identify" ? styles.identifyActiveButton : {}),
                }}
                onClick={() => identifyScreen(selectedScreenRecord)}
                disabled={busyAction === `identify-${selectedScreenRecord.screenCode}`}
              >
                <Monitor size={15} />
                {busyAction === `identify-${selectedScreenRecord.screenCode}`
                  ? "Updating..."
                  : selectedScreenRecord.status === "identify"
                    ? "Stop Locate"
                    : "Locate"}
              </button>
              <button
                type="button"
                style={styles.actionModalPrimary}
                onClick={() => sendScreenToPreServiceLoop(selectedScreenRecord)}
                disabled={busyAction === `loop-${selectedScreenRecord.screenCode}`}
              >
                <RotateCcw size={15} />
                {busyAction === `loop-${selectedScreenRecord.screenCode}` ? "Sending..." : "Pre-Service Loop"}
              </button>
              <button
                type="button"
                style={{ ...styles.actionModalSecondary, ...styles.dangerButton }}
                onClick={() => removeScreen(selectedScreenRecord)}
                disabled={busyAction === `remove-${selectedScreenRecord.screenCode}`}
              >
                <Trash2 size={15} />
                {busyAction === `remove-${selectedScreenRecord.screenCode}` ? "Removing..." : "Remove Screen"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showAddScreenModal ? (
        <div style={styles.modalOverlay} role="presentation" onMouseDown={() => setShowAddScreenModal(false)}>
          <div
            style={{ ...styles.modalCard, ...(isPhone ? styles.modalCardPhone : {}) }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-live-screen-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <div id="add-live-screen-title" style={styles.modalTitle}>Add {getScreenGroupLabel(addScreenGroup)}</div>
                <div style={styles.modalMeta}>
                  Use this link on the computer connected to your display. It will join {addScreenGroup === "interactive" ? "Interactive Screens" : "Service Screens"} automatically.
                </div>
              </div>
              <button type="button" style={styles.iconButton} onClick={() => setShowAddScreenModal(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div style={styles.instructionList}>
              <div style={styles.instructionStep}>1. On the display PC, open the live link below in Chrome, Edge, or another browser.</div>
              <div style={styles.instructionStep}>2. Name the screen when prompted. It will be assigned to {addScreenGroup === "interactive" ? "Interactive Screens" : "Service Screens"}.</div>
              <div style={styles.instructionStep}>3. Press F11 on Windows, or use the browser full-screen option, then leave the page open.</div>
            </div>

            <div style={styles.urlValue}>{getGroupedLiveUrl(display?.publicUrl, addScreenGroup)}</div>
            <div style={{ ...styles.urlActions, ...(isPhone ? styles.urlActionsPhone : {}) }}>
              <button type="button" style={styles.secondaryButton} onClick={() => copyGroupedLiveUrl(addScreenGroup)}>
                <Copy size={14} />
                Copy URL
              </button>
              <a href={getGroupedLiveUrl(display?.publicUrl, addScreenGroup)} target="_blank" rel="noreferrer" style={styles.linkButton}>
                <ExternalLink size={14} />
                Open Link
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  wrapper: {
    color: "#0f172a",
    minHeight: "100%",
    padding: "10px 0 28px",
    position: "relative",
  },
  wrapperPhone: {
    padding: "4px 0 calc(94px + env(safe-area-inset-bottom))",
  },
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 14,
  },
  embeddedContentShell: {
    display: "contents",
  },
  embeddedLiveDisplayContent: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minWidth: 0,
  },
  liveDisplayShell: {
    display: "block",
    position: "relative",
    zIndex: 1,
  },
  liveDisplayContent: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    marginLeft: 296,
    minWidth: 0,
    paddingRight: 20,
  },
  liveDisplayContentPhone: {
    marginLeft: 0,
    paddingRight: 0,
  },
  sideNav: {
    background: "rgba(255,255,255,0.76)",
    backdropFilter: "blur(18px) saturate(1.12)",
    WebkitBackdropFilter: "blur(18px) saturate(1.12)",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.58)",
    borderRadius: 24,
    bottom: 16,
    boxShadow: "0 18px 46px rgba(15,23,42,0.14)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    left: 16,
    padding: 12,
    position: "fixed",
    top: 0,
    width: 252,
    zIndex: 90,
  },
  sideNavPhone: {
    ...churchMobileNavStyles.dock,
    gridAutoRows: "minmax(54px, auto)",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  },
  sideNavBrand: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "6px 8px 8px",
  },
  sideNavBrandPhone: {
    display: "none",
  },
  sideNavBrandLine: {
    background: "linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb),0.28), transparent)",
    height: 1,
    width: "100%",
  },
  sideNavBrandLogo: {
    display: "block",
    height: "auto",
    maxHeight: 92,
    maxWidth: 196,
    objectFit: "contain",
    width: "90%",
  },
  sideNavOrg: {
    alignItems: "center",
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 18,
    boxShadow: "0 10px 22px rgba(15,23,42,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    margin: "0 0 6px",
    minWidth: 0,
    padding: "18px 14px 16px",
    textAlign: "center",
  },
  sideNavOrgPhone: {
    display: "none",
  },
  sideNavDivider: {
    background: "linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb),0.24), transparent)",
    height: 1,
    margin: "2px 8px 4px",
  },
  sideNavDividerPhone: {
    display: "none",
  },
  sideNavLogo: {
    display: "block",
    height: "auto",
    maxHeight: 146,
    maxWidth: 212,
    objectFit: "contain",
    width: "100%",
  },
  sideNavOrgCopy: {
    minWidth: 0,
    width: "100%",
  },
  sideNavOrgLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  sideNavOrgName: {
    color: "#0f172a",
    display: "block",
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.18,
    overflowWrap: "anywhere",
    whiteSpace: "normal",
  },
  navButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.09)",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "transparent",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 9,
    minHeight: 42,
    outline: "none",
    padding: "0 13px",
    textAlign: "left",
    width: "100%",
  },
  navMain: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 8,
    minHeight: 0,
    overflow: "auto",
  },
  navMainPhone: {
    display: "contents",
  },
  navButtonPhone: {
    ...churchMobileNavStyles.item,
  },
  navButtonActive: {
    color: "#fff",
    boxShadow: "none",
  },
  navButtonActivePhone: {
    ...churchMobileNavStyles.activeItem,
  },
  sideNavFooter: {
    borderTop: "1px solid rgba(var(--color-primary-rgb),0.14)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: "auto",
    paddingTop: 10,
  },
  sideNavFooterPhone: {
    display: "contents",
  },
  footerButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.12)",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "transparent",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 9,
    minHeight: 42,
    outline: "none",
    padding: "0 13px",
    textAlign: "left",
    width: "100%",
  },
  footerButtonPhone: {
    ...churchMobileNavStyles.item,
  },
  navLabelPhone: {
    ...churchMobileNavStyles.label,
  },
  pageHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
    marginBottom: 4,
  },
  pageHeaderPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  pageTitle: {
    color: "#0f172a",
    fontSize: "clamp(22px, 2.2vw, 34px)",
    fontWeight: 850,
    lineHeight: 1.08,
    margin: 0,
  },
  pageMeta: {
    color: "#64748b",
    fontSize: 14,
    fontWeight: 700,
    marginTop: 6,
  },
  topRow: {
    alignItems: "center",
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
  },
  sectionTitle: {
    color: "#0f172a",
    fontSize: "clamp(18px, 1.5vw, 22px)",
    fontWeight: 800,
  },
  serviceMeta: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
  },
  refreshButton: {
    background: "#eef8f5",
    border: "1px solid #c1dcd4",
    borderRadius: 12,
    color: "#245a4d",
    cursor: "pointer",
    fontWeight: 700,
    padding: "12px 16px",
  },
  managementHeader: {
    alignItems: "center",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(16px) saturate(1.08)",
    WebkitBackdropFilter: "blur(16px) saturate(1.08)",
    border: "1px solid #d8e5d0",
    borderRadius: 18,
    boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    marginBottom: 18,
    marginLeft: 296,
    minHeight: 108,
    overflow: "hidden",
    padding: "16px 18px",
    position: "sticky",
    top: 0,
    zIndex: 70,
  },
  managementHeaderPhone: {
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 12,
    marginLeft: 0,
    top: 4,
  },
  managementHeaderMain: {
    alignItems: "center",
    display: "flex",
    gap: 16,
    minWidth: 0,
    position: "relative",
    zIndex: 1,
  },
  managementHeaderMainPhone: {
    alignItems: "flex-start",
  },
  managementHeaderIcon: {
    display: "block",
    flex: "0 0 auto",
    height: 74,
    objectFit: "contain",
    width: 74,
  },
  managementHeaderCopy: {
    minWidth: 0,
  },
  managementHeaderEyebrow: {
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 950,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  managementHeaderName: {
    color: "#0f172a",
    fontSize: 28,
    fontWeight: 950,
    lineHeight: 1.08,
    marginTop: 5,
    overflowWrap: "anywhere",
  },
  managementHeaderMeta: {
    alignItems: "flex-end",
    display: "flex",
    flex: "0 0 auto",
    flexDirection: "column",
    gap: 7,
    maxWidth: 300,
    position: "relative",
    textAlign: "right",
    zIndex: 1,
  },
  managementHeaderMetaPhone: {
    alignItems: "flex-start",
    maxWidth: "100%",
    textAlign: "left",
    width: "100%",
  },
  managementHeaderWelcomeLabel: {
    color: "var(--color-primary-dark)",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  managementHeaderUserName: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 950,
    lineHeight: 1.1,
    overflowWrap: "anywhere",
  },
  managementHeaderIdRow: {
    alignItems: "center",
    background: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(95,125,77,0.14)",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 8,
    justifyContent: "flex-end",
    maxWidth: "100%",
    minWidth: 0,
    padding: "9px 12px",
  },
  managementHeaderIdRowPhone: {
    alignSelf: "flex-start",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },
  managementHeaderChurchName: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 850,
    lineHeight: 1.3,
    marginTop: 5,
    overflowWrap: "anywhere",
  },
  liveControllerToolbar: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d8e5d0",
    borderRadius: 18,
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
    padding: 16,
  },
  liveControllerToolbarPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  toolbarTitle: {
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 900,
  },
  toolbarMeta: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
  },
  toolbarActions: {
    display: "flex",
    gap: 10,
  },
  toolbarActionsPhone: {
    flexDirection: "column",
  },
  actionButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.10)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 12,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 8,
    justifyContent: "center",
    minHeight: 42,
    padding: "0 14px",
  },
  tileGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  groupTitle: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  cleanTileGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  },
  cleanTileGridPhone: {
    gridTemplateColumns: "1fr",
  },
  cleanTile: {
    appearance: "none",
    WebkitAppearance: "none",
    background: "#ffffff",
    border: "1px solid #d8e5d0",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
    color: "#0f172a",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    font: "inherit",
    gap: 10,
    minHeight: 148,
    padding: 16,
    textAlign: "left",
  },
  cleanTileHeader: {
    alignItems: "center",
    display: "flex",
    gap: 9,
    minWidth: 0,
  },
  cleanIconWrap: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.10)",
    borderRadius: 12,
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 34,
    justifyContent: "center",
    width: 34,
  },
  cleanTileLabel: {
    color: "#475569",
    fontSize: 12,
    fontWeight: 900,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  cleanTileValue: {
    color: "#0f172a",
    fontSize: "clamp(24px, 3vw, 36px)",
    fontWeight: 950,
    lineHeight: 1,
    overflowWrap: "anywhere",
  },
  cleanTileDetail: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
  },
  statsRow: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  },
  statsRowPhone: {
    gridTemplateColumns: "1fr",
  },
  statCard: {
    background: "#ffffff",
    border: "1px solid #d6e8e2",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
    padding: 16,
  },
  statLabel: {
    color: "#6b7280",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  statValue: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 900,
    marginTop: 8,
    textTransform: "capitalize",
  },
  overviewTileGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  overviewTileGridPhone: {
    gridTemplateColumns: "1fr",
  },
  overviewActionTile: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d6e8e2",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
    color: "#0f172a",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    gap: 14,
    minHeight: 112,
    padding: 16,
    textAlign: "left",
    width: "100%",
  },
  overviewActionTileBusy: {
    cursor: "wait",
    opacity: 0.72,
  },
  overviewActionTileIcon: {
    alignItems: "center",
    background: "#eff7f4",
    border: "1px solid #c1dcd4",
    borderRadius: 14,
    color: "#245a4d",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 48,
    justifyContent: "center",
    width: 48,
  },
  overviewActionTileContent: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },
  overviewActionTileTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  overviewActionTileBody: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
  },
  controllerSurface: {
    background: "rgba(255,255,255,0.94)",
    border: "1px solid #d6e8e2",
    borderRadius: 18,
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.07)",
    display: "grid",
    gap: 16,
    padding: 16,
  },
  controllerHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
  },
  controllerHeaderPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  controllerHeaderCopy: {
    minWidth: 0,
  },
  controllerHeaderActions: {
    display: "flex",
    flex: "0 0 auto",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  controllerHeaderActionsPhone: {
    flexDirection: "column",
  },
  controllerTileGrid: {
    display: "grid",
    gap: 18,
    gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
  },
  controllerInfoTile: {
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 18,
    boxShadow: "0 12px 26px rgba(23,32,51,0.08)",
    color: "#172033",
    display: "grid",
    gap: 14,
    minHeight: 230,
    padding: 20,
  },
  controllerInfoTileFull: {
    gridColumn: "1 / -1",
    minHeight: 0,
  },
  controllerTileTop: {
    alignItems: "center",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
    minWidth: 0,
  },
  controllerTileIcon: {
    alignItems: "center",
    background: "linear-gradient(135deg, #e7f5ef, #f4fbf8)",
    borderRadius: 14,
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 56,
    justifyContent: "center",
    width: 56,
  },
  controllerTileTitle: {
    color: "#172033",
    fontSize: 21,
    fontWeight: 900,
    lineHeight: 1.16,
  },
  controllerTileBody: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.45,
    overflowWrap: "anywhere",
  },
  controllerTileActions: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: "auto",
  },
  inlineTileButton: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 12,
    fontWeight: 900,
    gap: 7,
    justifyContent: "center",
    minHeight: 36,
    padding: "0 12px",
  },
  inlineTileLink: {
    alignItems: "center",
    background: "#eef8f5",
    border: "1px solid #c1dcd4",
    borderRadius: 999,
    color: "#245a4d",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    gap: 7,
    justifyContent: "center",
    minHeight: 36,
    padding: "0 12px",
    textDecoration: "none",
  },
  compactSwitchButton: {
    alignItems: "center",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 12,
    fontWeight: 900,
    justifyContent: "center",
    minHeight: 34,
    padding: "0 12px",
  },
  screenTile: {
    appearance: "none",
    WebkitAppearance: "none",
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 18,
    boxShadow: "0 12px 26px rgba(23,32,51,0.08)",
    color: "#172033",
    cursor: "pointer",
    display: "grid",
    gap: 16,
    gridTemplateRows: "auto minmax(50px, auto) 1fr",
    minHeight: 230,
    outline: "none",
    padding: 20,
    textAlign: "left",
    transition: "box-shadow 160ms ease, transform 160ms ease",
  },
  screenGroupSection: {
    display: "grid",
    gap: 12,
    gridColumn: "1 / -1",
  },
  screenGroupHeader: {
    alignItems: "center",
    background: "rgba(255,255,255,0.94)",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    boxShadow: "0 12px 28px rgba(23,32,51,0.08)",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    minHeight: 52,
    padding: "9px 10px 9px 18px",
  },
  screenGroupTitle: {
    color: "#172033",
    fontSize: 15,
    fontWeight: 950,
    lineHeight: 1.2,
  },
  screenGroupMeta: {
    color: "#667085",
    fontSize: 12,
    fontWeight: 750,
    lineHeight: 1.35,
    marginTop: 2,
  },
  screenGroupCount: {
    alignItems: "center",
    background: "#eef8f5",
    border: "1px solid #c1dcd4",
    borderRadius: 999,
    color: "#245a4d",
    display: "inline-flex",
    flex: "0 0 auto",
    fontSize: 13,
    fontWeight: 950,
    height: 30,
    justifyContent: "center",
    minWidth: 30,
    padding: "0 10px",
  },
  screenGroupGrid: {
    display: "grid",
    gap: 18,
    gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
  },
  emptyScreenGroup: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 18,
    color: "#667085",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.45,
    minHeight: 230,
    padding: 18,
  },
  addScreenTile: {
    alignItems: "center",
    alignContent: "center",
    background: "linear-gradient(135deg, rgba(255,255,255,0.54), rgba(248,250,252,0.28))",
    gap: 14,
    gridTemplateRows: "auto auto auto",
    justifyItems: "center",
    placeContent: "center",
    textAlign: "center",
  },
  addScreenIcon: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.10)",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    height: 84,
    justifyContent: "center",
    width: 84,
  },
  screenTileIcon: {
    alignItems: "center",
    background: "linear-gradient(135deg, #e7f5ef, #f4fbf8)",
    borderRadius: 14,
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 56,
    justifyContent: "center",
    width: 56,
  },
  screenStatusPill: {
    background: "#e7f5ef",
    borderRadius: 999,
    color: "#245a4d",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    maxWidth: "100%",
    overflow: "hidden",
    padding: "5px 9px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  screenStatusPillOffline: {
    background: "#f1f5f9",
    color: "#64748b",
  },
  screenTileTitle: {
    color: "#172033",
    fontSize: 21,
    fontWeight: 950,
    lineHeight: 1.16,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  },
  screenTileMeta: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.35,
  },
  screenTileDetails: {
    alignSelf: "stretch",
    display: "grid",
    gap: 9,
  },
  screenTileDetailRow: {
    alignItems: "center",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "82px minmax(0, 1fr)",
    minHeight: 22,
  },
  screenTileDetailLabel: {
    color: "#8a93a5",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  screenTileDetailValue: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 800,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyScreenTile: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 18,
    color: "#667085",
    display: "flex",
    fontSize: 14,
    fontWeight: 750,
    minHeight: 160,
    padding: 22,
  },
  controlCard: {
    background: "#ffffff",
    border: "1px solid #d6e8e2",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 16,
  },
  preServiceControlCard: {
    width: "100%",
  },
  interactiveControlCard: {
    width: "100%",
  },
  preServiceIntro: {
    borderBottom: "1px solid #e2e8f0",
    paddingBottom: 14,
  },
  interactivePreviewRow: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  interactivePreviewRowPhone: {
    gridTemplateColumns: "1fr",
  },
  interactivePreviewTile: {
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 18,
    boxShadow: "0 12px 26px rgba(23,32,51,0.08)",
    display: "grid",
    gap: 14,
    minHeight: 210,
    padding: 18,
  },
  interactiveAppGrid: {
    display: "grid",
    gap: 10,
  },
  interactiveAppCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 18,
    boxShadow: "0 12px 26px rgba(23,32,51,0.08)",
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
    minHeight: 76,
    padding: 14,
  },
  interactiveAppCopy: {
    minWidth: 0,
  },
  interactiveAppTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 900,
  },
  interactiveAppDescription: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: 4,
  },
  controlHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  controlHeaderPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  cardTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 800,
  },
  cardMeta: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 4,
  },
  codeBadge: {
    background: "#eff7f4",
    border: "1px solid #b9d8cd",
    borderRadius: 999,
    color: "#245a4d",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    padding: "10px 14px",
  },
  urlRow: {
    alignItems: "center",
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
  },
  urlRowPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  urlValue: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    color: "#0f172a",
    flex: 1,
    fontSize: 13,
    padding: "12px 14px",
    wordBreak: "break-all",
  },
  urlActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  urlActionsPhone: {
    flexDirection: "column",
  },
  primaryButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 999,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
  },
  secondaryButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
  },
  linkButton: {
    alignItems: "center",
    background: "#eef8f5",
    border: "1px solid #c1dcd4",
    borderRadius: 999,
    color: "#245a4d",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
    textDecoration: "none",
  },
  dangerButton: {
    background: "#fff7f7",
    borderColor: "#fecaca",
    color: "#b91c1c",
  },
  identifyActiveButton: {
    background: "#020617",
    borderColor: "rgba(var(--color-primary-rgb),0.72)",
    boxShadow: "0 10px 24px rgba(15,23,42,0.18)",
    color: "#ffffff",
    overflow: "hidden",
    position: "relative",
  },
  identifyButtonRipple: {
    border: "2px solid rgba(255,255,255,0.92)",
    borderRadius: 999,
    height: "240%",
    left: "50%",
    pointerEvents: "none",
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%) scale(0.18)",
    width: "124%",
    zIndex: 0,
  },
  identifyButtonRippleOne: {
    animation: "oikosScreenIdentifyButtonRipple 2.1s linear infinite",
  },
  identifyButtonRippleTwo: {
    animation: "oikosScreenIdentifyButtonRipple 2.1s linear 0.7s infinite",
    borderColor: "rgba(255,255,255,0.74)",
  },
  identifyButtonRippleThree: {
    animation: "oikosScreenIdentifyButtonRipple 2.1s linear 1.4s infinite",
    borderColor: "rgba(255,255,255,0.52)",
  },
  layout: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "minmax(0, 1fr) minmax(360px, 0.9fr)",
  },
  layoutSingle: {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
  layoutPhone: {
    gridTemplateColumns: "1fr",
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  loopList: {
    display: "grid",
    gap: 10,
  },
  loopCard: {
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 18,
    boxShadow: "0 12px 26px rgba(23,32,51,0.08)",
    cursor: "grab",
    padding: 12,
    transition: "box-shadow 160ms ease, transform 160ms ease",
  },
  loopCardDragging: {
    opacity: 0.48,
  },
  loopHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    minWidth: 0,
  },
  loopDragHandle: {
    alignItems: "center",
    color: "#94a3b8",
    display: "flex",
    flex: "0 0 auto",
    minHeight: 34,
  },
  loopTone: {
    background: "#e7f5ef",
    borderRadius: 999,
    color: "#245a4d",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.08em",
    maxWidth: "100%",
    overflow: "hidden",
    padding: "5px 9px",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  loopSummary: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  loopControls: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 auto",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  loopTimeLabel: {
    alignItems: "center",
    color: "#64748b",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 900,
    gap: 7,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  loopTimeSelect: {
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 800,
    height: 34,
    outline: "none",
    padding: "0 9px",
  },
  switchButton: {
    alignItems: "center",
    border: "1px solid",
    borderRadius: 999,
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 0 auto",
    fontSize: 11,
    fontWeight: 900,
    gap: 7,
    height: 34,
    justifyContent: "space-between",
    letterSpacing: "0.06em",
    minWidth: 72,
    padding: "4px 8px 4px 5px",
    textTransform: "uppercase",
  },
  switchButtonOn: {
    background: "#ecfdf5",
    borderColor: "#a7f3d0",
    color: "#166534",
  },
  switchButtonOff: {
    background: "#f8fafc",
    borderColor: "#cbd5e1",
    color: "#64748b",
  },
  switchButtonBusy: {
    cursor: "wait",
    opacity: 0.68,
  },
  switchKnob: {
    background: "#94a3b8",
    borderRadius: 999,
    boxShadow: "0 2px 5px rgba(15, 23, 42, 0.18)",
    display: "block",
    height: 22,
    transition: "background 160ms ease",
    width: 22,
  },
  switchKnobOn: {
    background: "#16a34a",
  },
  switchText: {
    minWidth: 24,
    textAlign: "center",
  },
  loopTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.22,
    marginTop: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  loopBody: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.4,
    marginTop: 6,
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    display: "-webkit-box",
  },
  actionStack: {
    display: "grid",
    gap: 10,
  },
  fieldLabel: {
    color: "#0f172a",
    display: "flex",
    flexDirection: "column",
    fontSize: 13,
    fontWeight: 700,
    gap: 8,
  },
  select: {
    background: "#f8fafc",
    border: "1px solid #d6e2ea",
    borderRadius: 12,
    color: "#0f172a",
    fontSize: 14,
    outline: "none",
    padding: "12px 14px",
  },
  timeInput: {
    background: "#f8fafc",
    border: "1px solid #d6e2ea",
    borderRadius: 12,
    color: "#0f172a",
    fontSize: 14,
    outline: "none",
    padding: "11px 14px",
  },
  sleepSettingsGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  sleepSettingsGridPhone: {
    gridTemplateColumns: "1fr",
  },
  sleepDays: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  sleepDayButton: {
    background: "#f8fafc",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    color: "#475569",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    minHeight: 34,
    padding: "0 12px",
  },
  sleepDayButtonActive: {
    background: "#020617",
    borderColor: "#020617",
    color: "#ffffff",
  },
  sleepPreviewBadge: {
    alignItems: "center",
    alignSelf: "flex-start",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 999,
    color: "#334155",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    gap: 7,
    minHeight: 34,
    padding: "0 12px",
  },
  manualControlCard: {
    background: "#f7fbfa",
    border: "1px solid #dceae6",
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 14,
  },
  manualHeader: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  manualHeaderPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  manualTitle: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 800,
  },
  manualMeta: {
    color: "#64748b",
    fontSize: 12,
    marginTop: 4,
  },
  slideCounter: {
    background: "#ffffff",
    border: "1px solid #d6e2ea",
    borderRadius: 999,
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 900,
    padding: "8px 12px",
  },
  manualButtons: {
    display: "flex",
    gap: 10,
  },
  manualButtonsPhone: {
    flexDirection: "column",
  },
  screenCount: {
    alignItems: "center",
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 6,
  },
  screenSummary: {
    background: "#f7fbfa",
    border: "1px solid #dceae6",
    borderRadius: 14,
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.6,
    padding: 14,
  },
  screenList: {
    display: "grid",
    gap: 10,
  },
  screenRow: {
    alignItems: "flex-start",
    background: "#f7fbfa",
    border: "1px solid #dceae6",
    borderRadius: 18,
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    padding: 14,
  },
  screenRowPhone: {
    flexDirection: "column",
  },
  screenRowMain: {
    flex: 1,
    minWidth: 0,
  },
  screenNameLabel: {
    color: "#0f172a",
    display: "flex",
    flexDirection: "column",
    fontSize: 11,
    fontWeight: 900,
    gap: 7,
    letterSpacing: "0.06em",
    minWidth: 0,
    textTransform: "uppercase",
    width: "100%",
  },
  screenNameInput: {
    background: "#ffffff",
    border: "1px solid #d6e2ea",
    borderRadius: 999,
    boxSizing: "border-box",
    color: "#0f172a",
    font: "inherit",
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0,
    maxWidth: "100%",
    minHeight: 42,
    minWidth: 0,
    outline: "none",
    padding: "0 16px",
    textTransform: "none",
    width: "min(320px, 100%)",
  },
  screenNameSaving: {
    alignSelf: "flex-start",
    background: "rgba(var(--color-primary-rgb),0.1)",
    border: "1px solid rgba(var(--color-primary-rgb),0.16)",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.08em",
    padding: "5px 9px",
    textTransform: "uppercase",
  },
  screenMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 650,
    lineHeight: 1.35,
    marginTop: 4,
    overflowWrap: "anywhere",
  },
  screenViewBadge: {
    background: "#eff7f4",
    border: "1px solid #b9d8cd",
    borderRadius: 999,
    color: "#245a4d",
    flex: "0 0 auto",
    fontSize: 11,
    fontWeight: 900,
    padding: "8px 10px",
    textTransform: "uppercase",
  },
  screenRowActions: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 auto",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
    maxWidth: 430,
  },
  screenRowActionsPhone: {
    alignItems: "stretch",
    width: "100%",
  },
  screenActionButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 12,
    fontWeight: 900,
    gap: 7,
    justifyContent: "center",
    minHeight: 40,
    padding: "0 14px",
    position: "relative",
    whiteSpace: "nowrap",
  },
  screenActionButtonContent: {
    alignItems: "center",
    display: "inline-flex",
    gap: 7,
    justifyContent: "center",
    position: "relative",
    zIndex: 1,
  },
  modalOverlay: {
    alignItems: "center",
    background: "rgba(15, 23, 42, 0.38)",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    padding: 18,
    position: "fixed",
    zIndex: 240,
  },
  modalCard: {
    background: "#ffffff",
    border: "1px solid #d6e8e2",
    borderRadius: 18,
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.24)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    maxWidth: 620,
    padding: 18,
    width: "min(620px, calc(100vw - 36px))",
  },
  screenModalCard: {
    background: "#ffffff",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 8,
    boxSizing: "border-box",
    boxShadow: "0 28px 70px rgba(15,23,42,0.28)",
    color: "#172033",
    display: "grid",
    gap: 18,
    maxWidth: 500,
    padding: 18,
    width: "min(500px, calc(100vw - 36px))",
  },
  modalCardPhone: {
    maxHeight: "calc(100dvh - 36px)",
    overflow: "auto",
  },
  modalHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    minWidth: 0,
  },
  modalTitle: {
    color: "#0f172a",
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1.1,
  },
  modalMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.4,
    marginTop: 5,
  },
  screenModalTitleBlock: {
    alignItems: "center",
    display: "flex",
    flex: "1 1 auto",
    gap: 12,
    minWidth: 0,
  },
  screenModalNameInput: {
    width: "100%",
  },
  screenModalDetails: {
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    display: "grid",
    gap: 10,
    padding: 14,
  },
  screenModalGroupActions: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "1fr 1fr",
  },
  groupChoiceButton: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 900,
    justifyContent: "center",
    minHeight: 42,
    padding: "0 12px",
  },
  groupChoiceButtonActive: {
    background: "#020617",
    borderColor: "#020617",
    color: "#ffffff",
  },
  screenModalActions: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "1fr 1fr",
  },
  actionModalSecondary: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
    padding: "0 14px",
  },
  actionModalPrimary: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "1px solid transparent",
    borderRadius: 999,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
    padding: "0 14px",
  },
  iconButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d6e2ea",
    borderRadius: 999,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 0 auto",
    height: 36,
    justifyContent: "center",
    padding: 0,
    width: 36,
  },
  instructionList: {
    display: "grid",
    gap: 8,
  },
  instructionStep: {
    background: "#f7fbfa",
    border: "1px solid #dceae6",
    borderRadius: 12,
    color: "#334155",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
    padding: "11px 12px",
  },
  previewCard: {
    background: "#ffffff",
    border: "1px solid #d6e8e2",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 16,
  },
  previewSurface: {
    alignItems: "center",
    aspectRatio: "16 / 9",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    color: "#0f172a",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden",
    padding: 8,
    position: "relative",
  },
  previewScreenFrame: {
    background: "#ffffff",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 12,
    boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.5)",
    boxSizing: "border-box",
    color: "#000000",
    fontSize: "clamp(6px, 1vw, 12px)",
    height: "100%",
    overflow: "hidden",
    position: "relative",
    width: "100%",
  },
  previewScreenLogo: {
    height: "12%",
    bottom: "5%",
    left: "4.8%",
    maxWidth: "22%",
    objectFit: "contain",
    position: "absolute",
    zIndex: 3,
  },
  previewScreenSlide: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    inset: 0,
    overflow: "hidden",
    padding: "16% 7% 56px",
    position: "absolute",
    textAlign: "center",
    width: "100%",
  },
  previewScreenSlideWelcome: {
    justifyContent: "center",
    paddingBottom: 56,
    paddingTop: "16%",
  },
  previewScreenSlideInfo: {
    justifyContent: "flex-start",
    paddingTop: "15%",
  },
  previewScreenSlideEvents: {
    paddingTop: "15%",
  },
  loopPreview: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    maxHeight: "100%",
    maxWidth: "min(92%, 760px)",
    overflow: "hidden",
    padding: "clamp(14px, 2.2vw, 28px)",
    textAlign: "center",
    width: "100%",
  },
  loopPreviewWelcome: {
    justifyContent: "center",
    paddingBottom: "clamp(24px, 6vw, 72px)",
    paddingTop: "clamp(34px, 10vw, 104px)",
  },
  loopPreviewInfo: {
    justifyContent: "flex-start",
    paddingTop: "clamp(44px, 10vw, 110px)",
  },
  loopPreviewEvents: {
    paddingTop: "clamp(40px, 9vw, 92px)",
  },
  verseSlide: {
    maxWidth: 620,
    textAlign: "center",
  },
  imagePreviewWrap: {
    alignItems: "center",
    display: "flex",
    height: "100%",
    justifyContent: "center",
    position: "relative",
    width: "100%",
  },
  previewHymnNameTile: {
    background: "rgba(255, 255, 255, 0.88)",
    border: "1px solid rgba(15, 23, 42, 0.14)",
    borderRadius: 12,
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.12)",
    boxSizing: "border-box",
    color: "#0f172a",
    fontSize: "clamp(13px, 1.5vw, 20px)",
    fontWeight: 900,
    left: 12,
    bottom: 12,
    maxWidth: "62%",
    overflow: "hidden",
    padding: "7px 14px",
    position: "absolute",
    textAlign: "left",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    zIndex: 2,
  },
  previewHymnNumberTile: {
    background: "rgba(255, 255, 255, 0.88)",
    border: "1px solid rgba(15, 23, 42, 0.14)",
    borderRadius: 12,
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.12)",
    boxSizing: "border-box",
    color: "#0f172a",
    fontSize: "clamp(13px, 1.5vw, 20px)",
    fontWeight: 900,
    padding: "7px 14px",
    position: "absolute",
    right: 12,
    bottom: 12,
    textAlign: "center",
    whiteSpace: "nowrap",
    zIndex: 2,
  },
  previewImageSlide: {
    borderRadius: 18,
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
  },
  previewTitle: {
    fontSize: "clamp(24px, 3vw, 38px)",
    fontWeight: 900,
    lineHeight: 1.08,
  },
  previewInfoKicker: {
    background: "rgba(15, 23, 42, 0.06)",
    border: "1px solid rgba(15, 23, 42, 0.1)",
    borderRadius: 999,
    color: "#334155",
    fontSize: "0.95em",
    fontWeight: 900,
    letterSpacing: "0.1em",
    marginBottom: "1.2em",
    padding: "0.55em 1.2em",
    textTransform: "uppercase",
  },
  previewInfoTitle: {
    borderBottom: "1px solid rgba(15, 23, 42, 0.16)",
    boxSizing: "border-box",
    color: "#000000",
    fontSize: "4.2em",
    fontWeight: 400,
    lineHeight: 0.98,
    maxWidth: "100%",
    overflow: "hidden",
    paddingBottom: "0.2em",
    textAlign: "center",
    width: "100%",
  },
  previewInfoSubtitle: {
    color: "#334155",
    fontSize: "1.75em",
    fontWeight: 500,
    lineHeight: 1.12,
    marginTop: "0.8em",
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "center",
  },
  previewInfoBody: {
    color: "#0f172a",
    fontSize: "2.35em",
    fontWeight: 400,
    lineHeight: 1.18,
    marginTop: "0.9em",
    maxWidth: "82%",
    overflow: "hidden",
    textAlign: "center",
    whiteSpace: "pre-line",
  },
  previewWelcomeTitle: {
    color: "#000000",
    fontSize: "6.8em",
    fontWeight: 400,
    lineHeight: 0.9,
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "center",
  },
  previewWelcomeChurchName: {
    color: "#000000",
    fontSize: "2.45em",
    fontWeight: 400,
    lineHeight: 1.04,
    marginTop: "0.7em",
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "center",
  },
  previewWelcomeBody: {
    color: "#000000",
    fontSize: "1.5em",
    fontWeight: 400,
    lineHeight: 1.35,
    marginTop: "1.2em",
    maxWidth: "82%",
    overflow: "hidden",
    textAlign: "center",
  },
  previewSubtitle: {
    fontSize: "clamp(18px, 2vw, 28px)",
    fontWeight: 800,
    lineHeight: 1.08,
    marginTop: 8,
  },
  previewBody: {
    fontSize: "clamp(15px, 1.6vw, 22px)",
    lineHeight: 1.5,
    marginTop: 18,
    whiteSpace: "pre-line",
  },
  previewFooterNote: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    marginTop: 18,
    textTransform: "uppercase",
  },
  previewEntryList: {
    display: "grid",
    gap: "0.9em",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    marginTop: "1.2em",
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "left",
    width: "100%",
  },
  previewHymnEntryList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.55em",
    marginTop: "1.2em",
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "left",
    width: "100%",
  },
  previewHymnEntryRow: {
    alignItems: "center",
    borderBottom: "1px solid rgba(15, 23, 42, 0.16)",
    display: "grid",
    gap: "0.8em",
    gridTemplateColumns: "4.2em minmax(0, 1fr)",
    padding: "0.45em 0",
  },
  previewHymnEntryNumber: {
    color: "#000000",
    fontSize: "1.45em",
    fontWeight: 700,
    lineHeight: 1,
  },
  previewHymnEntryMain: {
    minWidth: 0,
  },
  previewHymnEntryTitle: {
    color: "#000000",
    fontSize: "1.25em",
    fontWeight: 500,
    lineHeight: 1.18,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewHymnEntryLabel: {
    color: "#000000",
    fontSize: "0.68em",
    fontWeight: 900,
    letterSpacing: "0.08em",
    marginTop: "0.24em",
    opacity: 0.72,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  previewEntryCard: {
    background: "rgba(255, 255, 255, 0.52)",
    border: "1px solid rgba(15, 23, 42, 0.1)",
    borderRadius: 12,
    boxSizing: "border-box",
    minHeight: 0,
    overflow: "hidden",
    padding: "1em",
    paddingBottom: "2.25em",
    position: "relative",
  },
  previewEntryLabel: {
    bottom: "0.8em",
    color: "#000000",
    fontSize: "0.82em",
    fontWeight: 900,
    letterSpacing: "0.08em",
    maxWidth: "calc(100% - 2em)",
    overflow: "hidden",
    position: "absolute",
    right: "1em",
    textAlign: "right",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  previewEntryTitle: {
    color: "#000000",
    fontSize: "1.55em",
    fontWeight: 400,
    lineHeight: 1.22,
    marginTop: "0.35em",
    overflow: "hidden",
  },
  previewEntryMeta: {
    color: "#000000",
    fontSize: "1.05em",
    fontWeight: 400,
    lineHeight: 1.3,
    marginTop: "0.35em",
    overflow: "hidden",
  },
  previewEntryBody: {
    color: "#000000",
    fontSize: "1.05em",
    fontWeight: 400,
    lineHeight: 1.35,
    marginTop: "0.4em",
    maxHeight: "3.9em",
    overflow: "hidden",
    whiteSpace: "pre-line",
  },
  previewMoreText: {
    color: "#000000",
    fontSize: 12,
    fontWeight: 800,
  },
  previewVerseNumber: {
    fontSize: "0.66em",
    fontWeight: 800,
    marginRight: 6,
    opacity: 0.56,
    verticalAlign: "top",
  },
  previewLogo: {
    height: "clamp(34px, 8vw, 72px)",
    marginBottom: "clamp(8px, 1.6vw, 16px)",
    objectFit: "contain",
    width: "min(150px, 34vw)",
  },
  previewLogoEvents: {
    marginBottom: 4,
  },
  previewControls: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #dbe5ec",
    borderRadius: 14,
    display: "grid",
    gap: 10,
    gridTemplateColumns: "minmax(112px, 0.7fr) minmax(0, 1fr) minmax(112px, 0.7fr)",
    padding: 8,
  },
  previewControlsPhone: {
    gridTemplateColumns: "1fr",
  },
  previewArrow: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    gap: 8,
    minHeight: 44,
    justifyContent: "center",
    padding: "0 14px",
    width: "100%",
  },
  previewArrowPhone: {
    minHeight: 48,
  },
  previewCount: {
    alignItems: "center",
    color: "#475569",
    display: "flex",
    flexDirection: "column",
    fontSize: 12,
    gap: 3,
    fontWeight: 900,
    justifyContent: "center",
    minWidth: 0,
    textAlign: "center",
  },
  previewCountText: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  endOfSongBadge: {
    background: "rgba(254, 226, 226, 0.94)",
    border: "1px solid rgba(248, 113, 113, 0.42)",
    borderRadius: 999,
    color: "#991b1b",
    fontSize: 12,
    fontWeight: 900,
    left: 18,
    letterSpacing: "0.08em",
    padding: "8px 12px",
    position: "absolute",
    top: 18,
    textTransform: "uppercase",
  },
  emptyState: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.6,
  },
  error: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 700,
    padding: "12px 14px",
  },
  notice: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 12,
    color: "#166534",
    fontSize: 13,
    fontWeight: 700,
    padding: "12px 14px",
  },
};
